import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Calendar,
  FileText,
  User,
  Wallet,
  CreditCard,
  Zap,
  Award,
  TestTube,
  Users,
  Heart,
  Bell,
  Clock,
  Receipt,
  Sparkles,
  LifeBuoy,
} from 'lucide-react';
import { cn } from '@medikei/ui';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Zap, label: 'Consulta Expressa', path: '/expressa' },
  { icon: Calendar, label: 'Agendamentos', path: '/agendamentos' },
  { icon: Clock, label: 'Histórico', path: '/historico' },
  { icon: Receipt, label: 'Receitas', path: '/receitas' },
  { icon: Award, label: 'Atestados', path: '/atestados' },
  { icon: TestTube, label: 'Exames', path: '/exames' },
  { icon: Users, label: 'Dependentes', path: '/dependentes' },
  { icon: Heart, label: 'Favoritos', path: '/favoritos' },
  { icon: CreditCard, label: 'Pagamentos', path: '/pagamentos' },
  { icon: Wallet, label: 'Carteira', path: '/carteira' },
  { icon: Sparkles, label: 'Indique e Ganhe', path: '/parceiro', highlight: true },
  { icon: Bell, label: 'Notificações', path: '/notificacoes' },
  { icon: LifeBuoy, label: 'Suporte & Chamados', path: '/chamados' },
  { icon: FileText, label: 'Prontuários', path: '/medical-records' },
  { icon: User, label: 'Meu Perfil', path: '/profile' },
];

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onCloseMobile }: SidebarProps) {
  const showExpanded = !collapsed || mobileOpen;

  return (
    <>
      {/* Backdrop Mobile (< lg) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs lg:hidden transition-opacity"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'bg-card border-r border-border transition-all duration-300 flex flex-col',
          // Mobile Drawer: fixed overlay on small screens, static in-flow on lg+
          'fixed inset-y-0 left-0 z-50 lg:static lg:z-auto',
          mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0',
          collapsed ? 'lg:w-16' : 'lg:w-64',
          'w-64'
        )}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {showExpanded && (
            <img src="/logo.png" alt="Medikei" className="h-8 w-auto" decoding="async" />
          )}

          {/* Desktop Collapse Button */}
          <button
            onClick={onToggle}
            className="hidden lg:flex p-2 rounded-lg hover:bg-accent transition-colors"
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={onCloseMobile}
            className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors text-foreground"
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto no-scrollbar">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => onCloseMobile?.()}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                  'hover:bg-accent dark:hover:bg-accent/20',
                  isActive
                    ? 'bg-primary/10 text-primary dark:bg-primary/20 font-semibold'
                    : item.highlight
                    ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-500/10'
                    : ''
                )
              }
            >
              <item.icon
                size={20}
                className={item.highlight ? 'text-amber-500 shrink-0' : 'shrink-0'}
                aria-hidden="true"
              />
              {showExpanded && (
                <span className="text-sm font-medium flex items-center justify-between flex-1 truncate">
                  <span className="truncate">{item.label}</span>
                  {item.highlight && (
                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 shrink-0 ml-1">
                      PIX
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <button
            onClick={() => {
              localStorage.removeItem('accessToken');
              window.location.href = '/login';
            }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-colors w-full"
          >
            <LogOut size={20} aria-hidden="true" />
            {showExpanded && (
              <span className="text-sm font-medium">Sair</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
